import { useCallback, useMemo } from 'react'
import { FlaskConical } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { fetchAbResults, FF_STR as S, type AbResultRow } from './featureFlagsApi'

/**
 * A14.11 — Test natijalari (statistik ahamiyat). Har A/B eksperiment-variant
 * uchun konversiya + p-value + statistik ahamiyat. Natija-metrikasi telemetriya
 * kutadi → hozircha fail-soft bo'sh (endpoint paydo bo'lганda avtomatik to'ladi).
 * AUDIT_VIEW-gated (A14/settings moduli bilan bir xil huquq).
 */
export function AbTestResults() {
  const columns = useMemo<AdminColumn<AbResultRow>[]>(
    () => [
      {
        key: 'experiment',
        header: S.experiment,
        type: 'text',
        minWidth: 200,
        render: (r) => <span className="font-mono text-sm text-primary">{r.experiment}</span>,
      },
      {
        key: 'variant',
        header: S.variant,
        type: 'text',
        width: 140,
        render: (r) => <span className="font-mono text-xs text-secondary">{r.variant}</span>,
      },
      { key: 'users', header: S.users, type: 'number', width: 130 },
      { key: 'conversions', header: S.conversions, type: 'number', width: 120 },
      {
        key: 'conversion_rate',
        header: S.conversionRate,
        type: 'number',
        width: 120,
        align: 'right',
        accessor: (r) => r.conversion_rate,
        render: (r) => (
          <span className="tabular-nums text-secondary">{(r.conversion_rate * 100).toFixed(2)}%</span>
        ),
      },
      {
        key: 'p_value',
        header: S.pValue,
        type: 'number',
        width: 120,
        align: 'right',
        accessor: (r) => r.p_value,
        render: (r) => (
          <span className="font-mono text-xs tabular-nums text-secondary">{r.p_value.toFixed(4)}</span>
        ),
      },
      {
        key: 'significant',
        header: S.significance,
        type: 'enum',
        width: 140,
        accessor: (r) => (r.significant ? 'yes' : 'no'),
        options: [
          { value: 'yes', label: S.significant, status: 'active' },
          { value: 'no', label: S.notSignificant, status: 'pending' },
        ],
      },
    ],
    [],
  )

  const fetcher = useCallback(fetchAbResults, [])

  return (
    <>
      <PageHeader title={S.resultsTitle} screenId="A14.11" />
      <DataTable<AbResultRow>
        screenId="A14.11"
        permission={PERMISSIONS.AUDIT_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => `${r.experiment}::${r.variant}`}
        exportable
        emptyState={{ icon: <FlaskConical size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
